import type { MailMessageDetail } from "@/lib/types/mail";

const DB_NAME = "aurelia_mail_bodies_v1";
const DB_VERSION = 1;
const STORE = "bodies";
const MAX_IDB_ENTRIES = 2000;

export type BodyStoreRecord = {
  id: string;
  accountId: number;
  folder: string;
  uid: number;
  savedAt: number;
  message: MailMessageDetail;
};

let dbPromise: Promise<IDBDatabase> | null = null;

export function bodyStoreId(accountId: number, folder: string, uid: number): string {
  return `${accountId}\0${folder}\0${uid}`;
}

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error ?? new Error("IndexedDB indisponible."));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("accountId", "accountId", { unique: false });
        store.createIndex("savedAt", "savedAt", { unique: false });
        store.createIndex("accountFolder", ["accountId", "folder"], { unique: false });
      }
    };
  });

  return dbPromise;
}

function runTransaction<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | void
): Promise<T | void> {
  return openDb().then(
    (db) =>
      new Promise<T | void>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        const request = fn(store);

        tx.oncomplete = () => {
          if (request && "result" in request) {
            resolve((request as IDBRequest<T>).result);
          } else {
            resolve();
          }
        };
        tx.onerror = () => reject(tx.error ?? new Error("Transaction IndexedDB échouée."));
        if (request) {
          request.onerror = () => reject(request.error ?? new Error("Requête IndexedDB échouée."));
        }
      })
  );
}

async function enforceIdbQuota(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const index = store.index("savedAt");
    const countReq = store.count();

    countReq.onsuccess = () => {
      const extra = countReq.result - MAX_IDB_ENTRIES;
      if (extra <= 0) {
        resolve();
        return;
      }

      let removed = 0;
      const cursorReq = index.openCursor();

      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (!cursor || removed >= extra) {
          resolve();
          return;
        }
        cursor.delete();
        removed += 1;
        cursor.continue();
      };
      cursorReq.onerror = () => reject(cursorReq.error);
    };
    countReq.onerror = () => reject(countReq.error);
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbGetBody(
  accountId: number,
  folder: string,
  uid: number
): Promise<MailMessageDetail | null> {
  if (uid <= 0 || accountId <= 0) return null;

  const id = bodyStoreId(accountId, folder, uid);
  const record = (await runTransaction<BodyStoreRecord | undefined>("readonly", (store) =>
    store.get(id)
  )) as BodyStoreRecord | undefined;

  return record?.message ?? null;
}

export async function idbPutBody(
  accountId: number,
  folder: string,
  message: MailMessageDetail
): Promise<void> {
  if (message.uid <= 0 || accountId <= 0) return;

  const record: BodyStoreRecord = {
    id: bodyStoreId(accountId, folder, message.uid),
    accountId,
    folder,
    uid: message.uid,
    savedAt: Date.now(),
    message,
  };

  await runTransaction("readwrite", (store) => store.put(record));
  await enforceIdbQuota();
}

export async function idbDeleteBody(
  accountId: number,
  folder: string,
  uid: number
): Promise<void> {
  if (uid <= 0 || accountId <= 0) return;
  await runTransaction("readwrite", (store) =>
    store.delete(bodyStoreId(accountId, folder, uid))
  );
}

export async function idbRelocateBody(
  accountId: number,
  uid: number,
  fromFolder: string,
  toFolder: string
): Promise<void> {
  if (uid <= 0 || accountId <= 0 || fromFolder === toFolder) return;

  const oldId = bodyStoreId(accountId, fromFolder, uid);
  const existing = (await runTransaction<BodyStoreRecord | undefined>("readonly", (store) =>
    store.get(oldId)
  )) as BodyStoreRecord | undefined;

  if (!existing?.message) return;

  const newRecord: BodyStoreRecord = {
    ...existing,
    id: bodyStoreId(accountId, toFolder, uid),
    folder: toFolder,
    savedAt: Date.now(),
  };

  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    store.delete(oldId);
    store.put(newRecord);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbPrimeBodies(
  accountId: number,
  folder: string,
  uids: number[]
): Promise<BodyStoreRecord[]> {
  if (accountId <= 0 || uids.length === 0) return [];

  const targets = uids.filter((uid) => uid > 0);
  if (targets.length === 0) return [];

  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const hits: BodyStoreRecord[] = [];

    let pending = targets.length;
    if (pending === 0) {
      resolve([]);
      return;
    }

    for (const uid of targets) {
      const req = store.get(bodyStoreId(accountId, folder, uid));
      req.onsuccess = () => {
        const record = req.result as BodyStoreRecord | undefined;
        if (record?.message) hits.push(record);
        pending -= 1;
        if (pending === 0) resolve(hits);
      };
      req.onerror = () => reject(req.error);
    }

    tx.onerror = () => reject(tx.error);
  });
}

export async function idbClearAccount(accountId: number): Promise<void> {
  if (accountId <= 0) return;

  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const index = store.index("accountId");
    const req = index.openCursor(IDBKeyRange.only(accountId));

    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      cursor.delete();
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbClearAll(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const req = store.clear();
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbPutMany(
  records: Array<{ accountId: number; folder: string; message: MailMessageDetail }>
): Promise<void> {
  if (records.length === 0) return;

  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const now = Date.now();

    for (const { accountId, folder, message } of records) {
      if (message.uid <= 0 || accountId <= 0) continue;
      store.put({
        id: bodyStoreId(accountId, folder, message.uid),
        accountId,
        folder,
        uid: message.uid,
        savedAt: now,
        message,
      } satisfies BodyStoreRecord);
    }

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  await enforceIdbQuota();
}
